import { IterableMap } from "@redotech/react-util/component";
import { MultipleChoiceOption, Step } from "@redotech/redo-model/return-flow";
import { SeededRandom } from "@redotech/util/random";
import { stringHash } from "@redotech/util/string";
import * as blocked from "./blocked.module.css";
import * as multipleChoiceQuestion from "./multiple-choice-question.module.css";
import * as product from "./product.module.css";
import * as rejected from "./rejected.module.css";

import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { memo, useMemo } from "react";

export const MultipleChoiceStep = memo(function MultipleChoiceStep({
  step,
  selectOption,
  draftReturnId,
  stepIndex,
}: {
  step: Step.MultipleChoice;
  selectOption: (option: MultipleChoiceOption, index: number) => void;
  draftReturnId: string;
  stepIndex: number;
}) {
  const options = useMemo(() => {
    const seed = stringHash(`${draftReturnId}-${stepIndex}`);
    const random = new SeededRandom(seed);

    //copy array so we don't modify the original
    const optionsArrayCopy = step.options.map((option, sourceIndex) => ({
      ...option,
      sourceIndex,
    }));

    if (!step.shuffle) {
      return optionsArrayCopy;
    }

    let currentIndex = optionsArrayCopy.length;
    let randomIndex: number;
    while (currentIndex > 0) {
      randomIndex = random.integer(currentIndex);
      currentIndex--;

      // swap values
      [optionsArrayCopy[currentIndex], optionsArrayCopy[randomIndex]] = [
        optionsArrayCopy[randomIndex],
        optionsArrayCopy[currentIndex],
      ];
    }

    return optionsArrayCopy;
  }, [step.options, step.shuffle, draftReturnId, stepIndex]);

  return (
    <div className={multipleChoiceQuestion.wrapper}>
      <div className={product.questionHeader}>{step.message}</div>
      <div
        className={product.questionDescription}
        // TODO parse HTML safely
        dangerouslySetInnerHTML={{ __html: step.description ?? "" }}
      />
      <div className={multipleChoiceQuestion.grid}>
        <IterableMap items={options} keyFn={(_, i) => i}>
          {(option) => (
            <div
              className={multipleChoiceQuestion.questionButton}
              onClick={() => selectOption(option, option.sourceIndex)}
            >
              {option.name}
            </div>
          )}
        </IterableMap>
      </div>
    </div>
  );
});

export const RejectStep = memo(function RejectStep({
  step,
  complete,
}: {
  step: Step.Reject;
  complete: () => void;
}) {
  return (
    <div className={rejected.wrapper}>
      <div
        className={rejected.message}
        // TODO parse HTML safely
        dangerouslySetInnerHTML={{ __html: step.message }}
      />
      <div className={product.button}>
        <Button
          onClick={complete}
          size={ButtonSize.MEDIUM}
          theme={ButtonTheme.PRIMARY}
          w="full"
        >
          Continue
        </Button>
      </div>
    </div>
  );
});

export const BlockedStep = memo(function BlockedStep({
  step,
  complete,
}: {
  step: Step.Block;
  complete: () => void;
}) {
  return (
    <div className={blocked.wrapper}>
      <div
        className={blocked.message}
        // TODO parse HTML safely
        dangerouslySetInnerHTML={{ __html: step.message }}
      />
      <div className={product.button}>
        <Button
          onClick={complete}
          size={ButtonSize.MEDIUM}
          theme={ButtonTheme.PRIMARY}
          w="full"
        >
          Continue
        </Button>
      </div>
    </div>
  );
});
